// Contracts — javelin Session Viewer (2026-07-25).
// Менеджер/админ в разделе Локации открывает сессию → превью видео по шагам
// (1 геройская камера × 19 упражнений), обработанное on-demand на мини и
// закэшированное в R2. Worker: apps/api/src/javelin/session_viewer.ts.
// Client: apps/internal javelin island (раздел локаций → плеер сессии).
//
// Только менеджерские (auth:'user') эндпоинты имеют контракты. Мини-воркерные
// (auth:'public' + X-Preview-Secret: worker/next|blob|complete) — внутренний
// протокол мини↔воркер, под контракты не идут (бинарные/служебные).
import { z } from 'zod';

// ── Шаг манифеста: одно упражнение = одно сжатое видео геройской камеры ──
export const JavPreviewStepSchema = z.looseObject({
  step: z.number(),                       // индекс шага 0..18
  exercise: z.string().nullable().default(null), // название упражнения из Participant_info сессии
  duration_s: z.number().nullable().default(null),
});
export type JavPreviewStep = z.infer<typeof JavPreviewStepSchema>;

// ── Манифест сессии (лежит в javelin_session_previews.manifest_json) ──
// Видео шага камеры cam лежит в R2 <pid>/<session>/<cam>/step<NN>.mp4 —
// фронт тянет через sessionPreview/video?...&cam=<cam>&step=<NN>.
export const JavPreviewManifestSchema = z.looseObject({
  pid: z.string().default(''),
  session: z.string().default(''),
  batch: z.string().default(''),
  cameras: z.array(z.string()).default([]),           // ГОТОВЫЕ ракурсы, напр. ['P1','P4']
  cameras_available: z.array(z.string()).default([]), // ВСЕ камеры сессии (P1..P8) — можно догрузить
  default_cam: z.string().default(''),                // ракурс по умолчанию (P1)
  // false — у сессии нет sessionState на S3, имена камер взять неоткуда,
  // и номера присвоены по порядку GUID устройств. Ракурс P1 такой сессии
  // НЕ обязан совпадать с P1 остальных — UI обязан это показать.
  positions_known: z.boolean().default(true),
  num_steps: z.number().default(0),
  steps: z.array(JavPreviewStepSchema).default([]),
  participant: z.looseObject({}).default({}),
  // Шаги, для которых видео собрать НЕ удалось (битый исходник на S3,
  // слишком длинный дубль и т.п.). Раньше такой файл ронял весь ракурс, и
  // сессия не догружалась никогда; теперь ракурс отдаётся без него, а
  // менеджер видит в кабинете, чего не хватает и почему.
  damaged: z.array(z.object({
    step: z.number(),
    cam: z.string(),
    reason: z.string().default(''),   // человеческая формулировка для кабинета
    detail: z.string().default(''),   // технический текст (ffmpeg/boto) — для нас
  })).default([]),
  // Шаги, пережатые жёстче ради лимита заливки (100 МБ у воркера).
  reduced: z.array(z.object({
    step: z.number(),
    cam: z.string(),
    note: z.string().default(''),
  })).default([]),
});
export type JavPreviewManifest = z.infer<typeof JavPreviewManifestSchema>;

// ── POST /api/javelin/sessionPreview/request { participant_id } ──
// Ставит сессию в очередь (или возвращает готовый кэш). Роль-скоуп в хендлере.
export const JavPreviewRequestReqSchema = z.object({
  participant_id: z.coerce.string(),
});
export type JavPreviewRequestReq = z.infer<typeof JavPreviewRequestReqSchema>;

// ── POST /api/javelin/sessionPreview/requestCam { participant_id, cam } ──
// Догрузить ракурс on-demand (P2/P3/P5-P8). Демон досоздаёт недостающие камеры.
export const JavPreviewRequestCamReqSchema = z.object({
  participant_id: z.coerce.string(),
  cam: z.coerce.string(),
});
export type JavPreviewRequestCamReq = z.infer<typeof JavPreviewRequestCamReqSchema>;

// ── GET /api/javelin/sessionPreview/status?participant_id= ──
export const JavPreviewStatusQuerySchema = z.object({
  participant_id: z.coerce.string(),
});
export type JavPreviewStatusQuery = z.infer<typeof JavPreviewStatusQuerySchema>;

// Общий Res для request и status: текущее состояние очереди + manifest когда ready.
export const JavPreviewStatusResSchema = z.looseObject({
  participant_id: z.string().default(''),
  status: z.enum(['queued', 'processing', 'ready', 'error', 'none']).default('none'),
  manifest: JavPreviewManifestSchema.nullable().default(null),
  cams_want: z.array(z.string()).default([]),     // ракурсы, поставленные в обработку
  error: z.string().default(''),
  requested_at: z.string().default(''),
  ready_at: z.string().default(''),
});
export type JavPreviewStatusRes = z.infer<typeof JavPreviewStatusResSchema>;

// ── GET /api/javelin/sessionPreview/locationSessions?home_id= ──
// Сессии локации (участники с записанной сессией) + статус кэша превью каждой.
export const JavLocationSessionsQuerySchema = z.object({
  home_id: z.coerce.string(),
});
export type JavLocationSessionsQuery = z.infer<typeof JavLocationSessionsQuerySchema>;

export const JavLocationSessionSchema = z.looseObject({
  participant_id: z.coerce.string(),
  full_session_id: z.string().default(''),
  name: z.string().default(''),
  session_date: z.string().default(''),
  status: z.string().default(''),                 // статус участника (open/recording/packed/closed)
  preview_status: z.string().default(''),         // '' | queued | processing | ready | error
  cams_ready: z.number().default(0),              // сколько ракурсов уже в R2
  cams_want: z.number().default(0),               // сколько ожидается всего
  damaged: z.number().default(0),                 // шагов без видео (битый исходник и т.п.)
});
export type JavLocationSession = z.infer<typeof JavLocationSessionSchema>;

export const JavLocationSessionsResSchema = z.looseObject({
  home_id: z.string().default(''),
  mod_id: z.string().default(''),
  sessions: z.array(JavLocationSessionSchema).default([]),
});
export type JavLocationSessionsRes = z.infer<typeof JavLocationSessionsResSchema>;

// GET /api/javelin/sessionPreview/video?participant_id=&step=NN → бинарный stream
// (mp4 из R2 с поддержкой Range). Контракт — только query-shape, тело бинарное.
export const JavPreviewVideoQuerySchema = z.object({
  participant_id: z.coerce.string(),
  step: z.coerce.string(),
  cam: z.coerce.string().optional(),
});
export type JavPreviewVideoQuery = z.infer<typeof JavPreviewVideoQuerySchema>;
